<template>
  <div class="share-filter-modal">
    <Modal
      v-if="shareFilterModalVisible"
      :primary-button-disabled="
        isFilterGenerationError ||
        isGeneratingSharableURL ||
        isSendingInvitation
      "
      tertiary-button-label="Cancel"
      primary-button-label="Send Invitation"
      size="small"
      active
      @onClose="onCloseModal()"
      @onConfirm="onSendInvitationSubmit()">
      <template #header>
        <p>Share filter</p>
      </template>
      <template #content>
        <div class="share-filter-modal-content">
          <div v-if="isGeneratingSharableURL" ref="loadingScreen" name="body">
            <div class="loading-screen-area">
              <div class="loading-screen-area--title">
                <h5>Generating shareable link....</h5>
                <loading
                  class="loading-screen-area--loader"
                  :active.sync="isGeneratingSharableURL"
                  :is-full-page="false"
                  color="#0573AB" />
              </div>
            </div>
          </div>
          <div v-else-if="isFilterGenerationError">
            <div class="loading-screen-error">
              <h5>Unable to generate shareable URL, please try again.</h5>
              <Button
                appearance="tertiary"
                label="Retry"
                @onClick="generateSharableURL" />
            </div>
          </div>
          <div v-else name="body">
            <div class="share-filter-modal-content-area">
              <div class="share-filter-modal-content-area-title">
                Link to share
              </div>
              <div
                class="share-filter-modal-content-area-content"
                @click="copyToClipboard(urlComputed)">
                <span>{{ urlComputed }}</span>
                <Icon
                  class-name="share-filter-modal-content-area-content__icon"
                  size="24"
                  name="file-copy" />
              </div>
            </div>
            <div class="share-filter-modal-content-sent-invite-area">
              <div class="share-filter-modal-content-sent-invite-area-title">
                <span
                  class="share-filter-modal-content-sent-invite-area-title--bold">
                  Send invite to team members
                </span>
                <span
                  class="share-filter-modal-content-sent-invite-area-title--normal">
                  An email invitation will be send to the user with shareable
                  link
                </span>
              </div>
              <div class="share-filter-modal-content-sent-invite-area-content">
                <DropdownFilter
                  filtername="item"
                  :value="[]"
                  :share-filter-users-list="shareFilterUsersList"
                  :is-open="true"
                  @onChange="onDropdownChange($event)" />
                <div
                  class="share-filter-modal-content-sent-invite-area-content--tag-area">
                  <user-tag
                    v-for="(user, key) in selectedMember"
                    :key="key"
                    :name="user.displayName"
                    :apmid="user.employeeId"
                    @on-remove="onTagRemove(key)" />
                </div>
              </div>
            </div>
            <loading
              class="share-filter--loader"
              :active.sync="isSendingInvitation"
              :is-full-page="false"
              color="#0573AB" />
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
// Vue
import { mapState } from "vuex";

// Actions
import { userActions } from "@/actions";

// Components
import DropdownFilter from "./DropdownFilter.vue";
import UserTag from "./UserTag.vue";
import Loading from "vue-loading-overlay";
import { showToast } from "@/components/common/Toast";

// Helpers
import {
  copyToClipboardDebounced,
  getMsalIdToken,
  getMsalAccessToken,
} from "@/utils/helpers";
import AxiosFetch from "@/utils/axios";

export default {
  name: "ShareFilter",
  components: {
    DropdownFilter,
    Loading,
    UserTag,
  },
  props: {
    shareFilterModalVisible: {
      type: Boolean,
      default: true,
    },
    isFilterSaving: {
      type: Boolean,
      default: false,
    },
    appliedFilters: {
      type: [Array, Object],
      required: true,
    },
    shareFilterUsersList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isSendingInvitation: false,
      isGeneratingSharableURL: false,
      isFilterGenerationError: false,
      selectedMember: [],
      shareId: null,
      showingClipboardToast: false,
    };
  },
  computed: {
    ...mapState(["user"]),
    urlComputed() {
      return `${window.location.origin}/?filter_id=${this.shareId}`;
    },
    appliedFilterWatch() {
      return {
        filterDataByName: this.appliedFilters,
        shareFilterModalVisible: this.shareFilterModalVisible,
      };
    },
  },
  watch: {
    appliedFilterWatch: {
      deep: true,
      immediate: true,
      handler() {
        if (this.shareFilterModalVisible) {
          this.generateSharableURL();
        }
      },
    },
  },
  methods: {
    onCloseModal() {
      this.$emit("on-close");
      this.selectedMember = [];
    },
    copyToClipboard(value) {
      copyToClipboardDebounced(
        value,
        this.showingClipboardToast,
        showToast,
        this.$copyText
      );
    },
    onDropdownChange(value) {
      if (
        !this.selectedMember.some(
          (member) => member.employeeId === value.employeeId
        )
      ) {
        this.selectedMember.push(value);
      }
    },
    onTagRemove(idx) {
      this.selectedMember.splice(idx, 1);
    },
    async onSendInvitationSubmit() {
      this.isSendingInvitation = true;
      const selectedMember = this.selectedMember.map((member) => {
        return {
          email_id: member.mail,
          name: member.displayName,
        };
      });
      const opts = userActions.triggerEmailForShareFilter(
        this.shareId,
        selectedMember,
        this.user?.displayName,
        `Bearer ${await getMsalIdToken(this)}`,
        `Bearer ${await getMsalAccessToken(this)}`
      );

      try {
        await AxiosFetch(opts);
        this.isSendingInvitation = false;
        this.selectedMember = [];
        this.onCloseModal();
        showToast(
          "success",
          "Shareable filter link has been emailed successfully."
        );
      } catch (error) {
        this.isSendingInvitation = false;
        this.selectedMember = [];
        showToast(
          "error",
          "Unable to send email invitation, please try again!"
        );
      }
    },
    async generateSharableURL() {
      this.isGeneratingSharableURL = true;
      this.isFilterGenerationError = false;
      const opts = userActions.createShareFilter(
        this.appliedFilters,
        `Bearer ${await getMsalIdToken(this)}`,
        `Bearer ${await getMsalAccessToken(this)}`
      );

      try {
        const {
          data: { share_id },
        } = await AxiosFetch(opts);
        this.shareId = share_id;
        this.isGeneratingSharableURL = false;
        this.isFilterGenerationError = false;
      } catch (error) {
        this.isGeneratingSharableURL = false;
        this.isFilterGenerationError = true;
        showToast(
          "error",
          "Unable to generate shareable URL, please try again!"
        );
      }
    },
  },
};
</script>

<style lang="scss">
.share-filter-modal {
  .modal--small {
    height: 450px;
  }
  &-content {
    &-area {
      &-content {
        background-color: var(--Gray-100);
        border: 1px solid var(--Gray-400);
        border-radius: 0.25rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem;
        margin-top: 0.5rem;

        &__icon {
          cursor: pointer;
        }
      }
    }
    &-sent-invite {
      &-area {
        margin-top: 0.5rem;
        padding: 0.5rem;
        border: 1px solid var(--Gray-400);
        border-radius: 4px;

        &-title {
          &--bold {
            display: block;
            font-family: "Maersk Text Bold";
            font-weight: bold;
          }
        }
        &-content {
          margin-top: 0.5rem;
        }
      }
    }
    & .loading-screen-error {
      margin-top: 1rem;

      & .button--tertiary {
        margin-top: 1rem;
      }
    }
  }
  & .vs__dropdown-menu {
    li {
      cursor: pointer;
      font-family: "Maersk Text";
      font-size: 0.875rem;
      margin: 0.5rem;
    }
  }
}
</style>
